import React, { useContext, useEffect, useMemo, useState } from 'react';
import { emptyArray, noop } from '@fabric-ui/utils';
import { SanitizeHTML } from '@utils/UtilsV2';
import useDebounce from '../../../../hooks/useDebounce';
import { sanitize } from '../../../../utils/utils';
import AvRichText from '../../../Tickets/AvRichText';
import { CustomDashboardContext } from '../../CustomDashboardContext';
import { TextCategoryWidgetTypes } from '../../types';
import { TextSubType } from '../../types/TextWidget.types';

const DEBOUNCE_MS = 300;

interface TextWidgetProps {
  widget: TextCategoryWidgetTypes;
  data?: Record<string, any>[];
  isLoading?: boolean;
  isEdit?: boolean;
}

const TextWidget: React.FC<TextWidgetProps> = ({ widget, data = emptyArray, isEdit = false }) => {
  const { setNewWidget } = useContext(CustomDashboardContext);

  // Local state so the editor stays responsive; context is only updated after the debounce.
  const [localText, setLocalText] = useState(widget.type === TextSubType.Text ? widget.definition.custom.text : '');
  const { debouncedValue: debouncedText } = useDebounce(localText, DEBOUNCE_MS);

  // Sync local state when a different widget is opened for edit.
  useEffect(() => {
    if (widget.type === TextSubType.Text) {
      setLocalText(widget.definition.custom.text);
    }
  }, [widget.id]);

  // Push debounced value to context so other widgets/saves pick it up.
  useEffect(() => {
    if (widget.type !== TextSubType.Text) {
      return;
    }
    const sanitized = sanitize(debouncedText);
    if (sanitized !== widget.definition.custom.text) {
      setNewWidget({
        ...widget,
        definition: {
          ...widget.definition,
          custom: {
            ...widget.definition.custom,
            text: sanitized,
          },
        },
      });
    }
  }, [debouncedText]);

  const renderTextWidget = useMemo(
    () => ({
      [TextSubType.Text]: () => (
        <div>
          {isEdit ? (
            <AvRichText
              onClick={noop}
              onFocus={noop}
              showBar
              value={localText}
              onChange={setLocalText}
              placeholder="Add your text here..."
              // onKeyDown={onKeyDown}
              // @ts-ignore
              fileUploadProps={{ enable: false }}
            />
          ) : (
            <SanitizeHTML html={widget.definition.custom.text.replace(/&nbsp;|\u00A0/g, ' ')} openLinksInNewTab noNetworkTags />
          )}
        </div>
      ),
    }),
    [data, widget, localText]
  );
  return renderTextWidget[widget.type]();
};

export default TextWidget;
